import MarketingStatus from "enums/marketingStatus";
import createPictureImageSources from "utils/createPictureImageSources";

export default class PaidMasterPlannedCommunityCard {
/**
 * Generates and sets the big image sources for different breakpoints.
 * Creates picture element sources with specified dimensions and parameters for responsive images.
 * Falls back to errorImage if thumbnail is not available.
 * 
 */
  getBigImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '1.5x': {
          width: 447,
          height: 297
        },
        '2x': {
          width: 400,
          height: 312
        },
        '3x': {
          width: 900,
          height: 600
        }
      },
      irsParameters: {
        mode: 'crop',
        progressive: true
      }
    });

    this.bigSources = sources;
    this.src = src;
  }

  /**
   * Generates and sets the small image sources for different breakpoints.
   * Creates picture element sources with specified dimensions and parameters for responsive images.
   * Falls back to errorImage if thumbnail is not available.
   * 
   */
  getSmallImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '230w': {
          maxwidth: 230,
          maxheight: 200
        },
        '434w': {
          maxwidth: 434,
          maxheight: 334
        },
        '541w': {
          maxwidth: 541,
          maxheight: 441
        },
        '632w': {
          maxwidth: 632,
          maxheight: 532
        },
        '716w': {
          maxwidth: 716,
          maxheight: 614
        }
      },
      irsParameters: {
        progressive: true
      }
    });

    this.smallSources = sources;
    this.src = src;
  }

  /**
   * Sets the metadata for the paid community card component.
   * Initializes card properties with provided metadata values and triggers image source generation.
   */
  setMetadata(metadata) {
    this.thumbnail = metadata?.thumbnail;
    this.communityName = metadata?.communityName;
    this.priceText = metadata?.priceText;
    this.id = metadata?.id;
    this.url = metadata?.url;
    this.builderId = metadata?.builderId;
    this.isFeaturedListing = metadata?.isFeaturedListing;
    this.builderCount = metadata?.builderCount;
    this.communityCount = metadata?.communityCount;
    this.city = metadata?.city;
    this.stateAbbreviation = metadata?.stateAbbreviation;
    this.zip = metadata?.zip;
    this.communityStatus = metadata?.communityStatus;
    this.previousPrice = metadata?.previousPrice;
    this.isHotDeal = metadata?.isHotDeal;
    this.errorImage = null;
    this.brandName = metadata?.brandName;
    this.priceLow = metadata?.priceLow;
    this.priceHigh = metadata?.priceHigh; 

    this.getBigImageSources();
    this.getSmallImageSources();
  }
  
  /**
   * Renders a status label for a paid community card based on priority conditions.
   * Checks conditions in order: featured listing (spotlight), coming soon status, or hot deal.
   * Returns the first matching status as an HTML string with appropriate styling.
   * 
   * @returns {string} HTML string containing the status label div, or empty string if no conditions match
   */
  renderStatusLabel() {
    const statusConfig = [
      {
        condition: this.isFeaturedListing,
        class: "spotlight",
        label: "Spotlight",
        qa: "spotlight"
      },
      {
        condition: this.communityStatus === MarketingStatus.ComingSoon.toString(),
        class: "coming-soon",
        label: "Coming soon",
        qa: "coming-soon"
      },
      {
        condition: this.isHotDeal,
        class: "hot-deal",
        label: "Hot Deal",
        qa: "hot-deal"
      }
    ];
  
    const match = statusConfig.find(s => s.condition);
    if (!match) {
      return "";
    }
  
    return `
      <div class="nhs-u-absolute--top-left-300">
        <span class="nhs-c-listing-status nhs-c-listing-status--${match.class}" data-qa="${match.qa}">
          ${match.label}
        </span>
      </div>
    `;
  }

  /**
   * Renders a responsive picture element with multiple source formats for different screen sizes.
   */
  renderPicture() {
    return `
      <picture class="nhs-n1-c-card__image nhs-c-image--ratio-16-9">

        ${this.smallSources
          .map(
            source =>
              `<source media="(max-width: 767px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

          
        ${this.bigSources
          .map(
            source =>
              `<source media="(min-width: 768px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

        <img src="${this.src}"
            class="nhs-c-image--ratio-16-9"
            alt="${this.communityName} - Master planned community in ${this.city}, ${this.stateAbbreviation} ${this.zip}"
            loading="eager"
            onerror="document.dispatchEvent(
              new CustomEvent('image-load-error', {
                bubbles: true,
                detail: { image: this }
              })
            )"
            onload="this.dataset.load=true">
      </picture>
    `;
  }

  /**
   * Returns the call to action based on the summary card properties.
   * @returns {string} The call to action text.
   */
  getCallToActionText() {
    if (this.communityStatus === MarketingStatus.ComingSoon.toString()) {
      return 'Get more information';
    }

    return 'Free brochure';
  }

  /**
   * Returns the lead generator opener based on the summary card information.
   * @returns {string} The lead generator opener.
   */
  getLeadGeneratorOpener() {
    if (this.communityStatus === MarketingStatus.ComingSoon.toString()) {
      return 'more-information-paid';
    }

    return 'get-brochure-paid';
  }

  /**
   * Generates the HTML markup for a lead generator button for the community card.
   *
   * @returns {string} HTML string for the lead generator button, including relevant data attributes.
   */
  getCardButton() {
    return `
      <button
        class="nhs-c-button--primary-lead nhs-u-margin--top-auto nhs-c-button--small-32 nhs-c-button--text-only"
        data-button-action="${this.getCallToActionText()}"
        data-url="${this.url}"
        data-variation="paid"
        data-from-page="CommunityResults.MapCard.MasterPlanCommunity"
        data-lead-generator-opener="${this.getLeadGeneratorOpener()}">
        ${this.getCallToActionText()}
      </button>
    `;
  }

/**
 * Generates the HTML markup for a paid community card component.
 * 
 * The card displays property information including images, pricing, specifications (bedrooms, bathrooms),
 * location details, and interactive elements such as favorite buttons.
 */
  getHtml() {
    const classNames = ['nhs-n1-c-card--housing', 'nhs-n1-c-card--paidlisting', 'nhs-n1-c-card--surface'];
    if (this.isFeaturedListing) {
      classNames.push('nhs-n1-c-card--spotlight');
    }

    return `
      <div 
        id="${this.id}"
        class="nhs-c-tooltip--map nhs-c-tooltip--card"
        data-standard-card
        data-card
        data-map-card
        data-id="${this.id}"
        data-type="community"
        data-paid-community
        data-is-basic="false"
        data-is-paid="true"
        data-community-id="${this.id}"
        data-builder-id="${this.builderId}"
        data-brand-name="${this.brandName}"
        data-community-name="${this.communityName}"
        data-marketing-status-label="${this.communityStatus}"
        ${this.isFeaturedListing ? `data-is-featured-listing = true` : ''}
        data-image-url="${this.thumbnail}"
        data-price-low="${this.priceLow}"
        data-price-high="${this.priceHigh}"
        data-city="${this.city}"
        data-state-abbreviation="${this.stateAbbreviation}"
        data-zip="${this.zip}">
        <div class="${classNames.join(' ')}">
            <div class="nhs-n1-c-card__container">
                ${this.renderPicture()}
                ${this.renderStatusLabel()}
                <div class="nhs-n1-c-card__body" data-qa="card_body">
                  <div class="nhs-n1-c-card__content">
                    <h3 class="nhs-o-typography--souvenir-subhead-2-large-light nhs-u-text--ellipsis">
                      <a href="${this.url}" target="_blank"
                        data-link
                        data-qa="community-name">${this.communityName}
                      </a>
                    </h3>
                      <div class="nhs-u-flex-column nhs-u-gap--200 nhs-u-width--full">
                        <div class="nhs-u-flex-row nhs-u-gap--200">
                          <p class="nhs-n1-c-card__price" data-card-element="price">
                            <span data-qa="price_label"> ${this.priceText} </span>
                          </p>
                          ${this.previousPrice !== '' && this.previousPrice ? `
                          <div class="nhs-n1-c-card__badge nhs-n1-c-card__badge--reduced nhs-n1-c-card__badge--strikethrough" data-qa="strike-thru-price">
                            ${this.previousPrice}
                          </div>
                          ` : ''}
                        </div>
                        <ul class="nhs-n1-c-card__stats" data-qa="card_specs">
                          ${this.communityCount ? `
                          <li class="nhs-n1-c-card__stats-stat">
                            <div class="nhs-n1-c-card__stats-stat-icon-box">
                              <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                <path fill="currentColor" d="M6.276 2.5a.5.5 0 0 1 .304.098l3.241 2.4 1.094.81.004.004 1.061.786.076.068a.501.501 0 0 1-.583.787l-.087-.052-.269-.2v5.252a1 1 0 0 1-.998 1c-1.06.003-2.386.013-3.715.023-1.327.01-2.658.02-3.72.022a1 1 0 0 1-1-.998V7.15l-.381.293a.5.5 0 1 1-.608-.794L2.037 5.62l3.937-3.018a.5.5 0 0 1 .302-.103m3.55-.083a.5.5 0 0 1 .528-.026l.08.055 3.82 3.235.003.002 1.183 1.002a.5.5 0 0 1-.647.763l-.361-.307V12a1 1 0 0 1-1 1H12.5v-1h.932V6.294l-.275-.231-3.012-2.552-.37.383-.77-.642.747-.772zM2.683 6.385v6.113c.684-.002 1.48-.009 2.316-.014V8.332a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v4.134c.762-.005 1.487-.01 2.117-.012V6.46L6.284 3.626zM6 12.332h1v-3.5H6z"></path>
                              </svg>
                            </div>
                            <span> <span class="nhs-n1-c-card__stats-stat-count">${this.communityCount}</span> ${this.communityCount === 1 ? 'Community' : 'Communities'} </span>
                          </li>
                            ` : ''}
                          ${this.builderCount ? `
                          <li class="nhs-n1-c-card__stats-stat">
                            <div class="nhs-n1-c-card__stats-stat-icon-box">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" class="nhs-n1-c-card__stats-stat-icon-svg" viewBox="0 0 16 16"><g clip-path="url(#a)">
                                <path fill="#397e83" d="M10.5 4.562c2.065.976 3.5 3.125 3.5 5.62q-.001.194-.014.363L15 11.75V14H1v-2.25l1.005-1.365L2 10.181c0-2.494 1.434-4.643 3.5-5.62L5.5 3.438l.203-.15C6.376 2.79 7.158 2.5 8 2.5s1.624.291 2.297.787l.203.15zm-1-.61a2.9 2.9 0 0 0-1-.404v3.451h-1V3.548a2.9 2.9 0 0 0-1 .405v5.046a.5.5 0 0 1-1 0V5.697A5.2 5.2 0 0 0 3.007 9.91l-.006.27c0 .049 0 .097.003.17l.012.346-.205.28L2 12.078V13h12v-.886l-.778-.924-.26-.309.026-.402q.01-.15.011-.297c0-1.93-1.012-3.59-2.499-4.482v3.3a.5.5 0 1 1-1 0zM11 10.5a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1z"></path></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h16v16H0z"></path></clipPath></defs>
                              </svg>
                            </div>
                            <span><span class="nhs-n1-c-card__stats-stat-count">${this.builderCount}</span> ${this.builderCount === 1 ? 'Builder' : 'Builders'} </span>
                          </li>
                            ` : ''}
                        </ul>
                        <ul class="nhs-n1-c-card__stats" data-qa="card_specs">
                          <li class="nhs-n1-c-card__stats-stat nhs-u-width--full">
                            <div class="nhs-n1-c-card__stats-stat-icon-box">
                              <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                <path stroke="currentColor" stroke-linejoin="round" d="M12.692 14.5H5.5a.524.524 0 0 1-.522-.526L5 1.544m7.714 2.865L12.692 15M3.524 1 14 4.857"></path><path fill="#fff" d="m2 11.266 2.738-2.143 2.833 2.143v3.331c0 .29-.233.526-.522.526H2.522A.524.524 0 0 1 2 14.597z"></path>
                                <path fill="#fff" d="M3.714 12.124h1.929v3H3.714z"></path>
                                <path stroke="#397e83" stroke-linejoin="round" d="m2 11.266 2.738-2.143 2.833 2.143v3.331c0 .29-.233.526-.522.526H2.522A.524.524 0 0 1 2 14.597z"></path>
                                <path stroke="#397e83" stroke-linejoin="round" d="M3.714 12.124h1.929v3H3.714z"></path><path fill="#397e83" d="M7.143 4.838h1.286v1.286H7.143zM9.714 6.981H11v1.286H9.714zM7.143 6.981h1.286v1.286H7.143zM9.714 9.123H11v1.286H9.714zM9.714 11.266H11v1.286H9.714z"></path>
                              </svg>
                            </div>
                            <span class="nhs-u-text--ellipsis" data-qa="community_location"> Master planned community in ${this.city}, ${this.stateAbbreviation} ${this.zip} </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                </div>
                ${this.getCardButton()}
            </div>
        </div>
      </div>
  `;
  }
}